import { Formik, FormikValues } from "formik";
import { Modal } from "../Modal";
import { Dropdown } from "@/crud/form/Dropdown";
import { Switch } from "@/crud/form/Switch";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { BASE_URL } from "@/dashboardQueries";
import { Input } from "@/crud/form/Input";
import { Quill } from "@/crud/form/Quill/Quill";
import { EmailTemplate } from "@/models/accreditation";
import { Copy, Send } from "lucide-react";
import yup from "@/crud/yup-extended";

interface Props {
  to?: string;
  subject?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  variables?: Record<string, string>;
  onSubmit: (values: FormikValues) => void; // Add onSubmit prop
}

export const SendEmailFromTemplateModal = ({
  to,
  subject,
  open,
  setOpen,
  variables,
  onSubmit, // Destructure onSubmit
}: Props) => {
  const [sendFromTemplate, setSendFromTemplate] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null); // Track the copied key
  const queryClient = useQueryClient();

  // Fetch the list of templates
  const { data: templates, isLoading: templatesLoading } = useQuery(
    "emailTemplates",
    () =>
      axios
        .get(`${BASE_URL}/dashboard/email-templates/list`)
        .then((res) => res.data),
    {
      enabled: sendFromTemplate, // Only fetch when sendFromTemplate is true
    }
  );

  // Fetch template details when a specific template is selected
  const fetchTemplateDetails = (templateId: string) =>
    axios
      .get(`${BASE_URL}/dashboard/email-templates/${templateId}`)
      .then((res) => res.data);

  return (
    <Modal isOpen={open} setIsOpen={setOpen}>
      <div className="w-full max-w-3xl p-6">
        <h4 className="dark:text-gray-400 font-semibold text-sm mb-3">
          Send email
        </h4>

        <Formik
          initialValues={{
            sendFromTemplate: false,
            template: {},
            to: to,
            subject: subject,
            body: "",
          }}
          validationSchema={yup.object({
            to: yup.string().email("Invalid email").required("Required"),
            subject: yup.string().required("Required"),
            body: yup.string().required("Required"),
          })}
          onSubmit={(values: FormikValues) => {
            onSubmit(values); // Call the onSubmit prop
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            // to allow us to access send from template outside of the form
            useEffect(() => {
              setSendFromTemplate(values.sendFromTemplate);

              // if they don't want to send from a template, clear subject and body
              if (values.sendFromTemplate) {
                setFieldValue("subject", "");
                setFieldValue("body", "");
              }
            }, [values.sendFromTemplate]);

            // Fetch template details when the template changes
            const { data: templateDetails, refetch: refetchTemplateDetails } =
              useQuery(
                ["emailTemplateDetails", values.template?.id],
                () => fetchTemplateDetails(values.template.id),
                {
                  enabled: false, // Disabled by default, only fetch when triggered
                }
              );

            useEffect(() => {
              if (values.template?.id) {
                refetchTemplateDetails().then((res) => {
                  if (res.data) {
                    setFieldValue("subject", res.data.subject);
                    setFieldValue("body", res.data.body);
                  }
                });
              }
            }, [values.template?.id]);

            return (
              <form onSubmit={handleSubmit}>
                <div className="">
                  <div className="">
                    <div className="flex mt-4">
                      <div className="flex grow flex-col">
                        <span className="text-sm/6 font-medium text-gray-900 dark:text-white">
                          Send email from template
                        </span>
                        <span className="text-xs text-gray-500">
                          Write your own email, or start from an existing
                          template
                        </span>
                      </div>
                      <div className="flex-1 justify-end flex">
                        <Switch name="sendFromTemplate" />
                      </div>
                    </div>

                    {values.sendFromTemplate && templates && (
                      <div className="mt-4">
                        <label
                          htmlFor="template"
                          className="block text-sm/6 font-medium dark:text-white mb-2"
                        >
                          Template
                        </label>
                        <Dropdown
                          name="template"
                          options={
                            templatesLoading
                              ? [{ id: "", value: "Loading..." }]
                              : templates.map((template: any) => ({
                                  id: template.id,
                                  value: template.name,
                                }))
                          }
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm/6 font-medium dark:text-white mb-2"
                        >
                          To
                        </label>
                        <Input
                          name="to"
                          type="text"
                          value={values.to}
                          required
                        />
                      </div>
                      <div className="mt-4">
                        <label
                          htmlFor="subject"
                          className="block text-sm/6 font-medium dark:text-white mb-2"
                        >
                          Subject
                        </label>
                        <Input
                          name="subject"
                          type="text"
                          value={values.subject}
                          required
                        />
                      </div>

                      {variables && (
                        <div className="mt-4">
                          <h5 className="text-sm font-medium dark:text-white mb-2">
                            Available Variables
                          </h5>
                          <dl className="divide-y divide-gray-100 dark:divide-white/10">
                            {Object.entries(variables).map(([key, value]) => (
                              <div
                                key={key}
                                className="px-4 mt-1 sm:grid sm:grid-cols-3 sm:px-0 py-1"
                              >
                                <dt className="text-sm/6 font-medium text-gray-900 dark:text-white flex">
                                  <pre>{key}</pre>
                                  <div className="flex flex-1 justify-end pr-4">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard
                                          .writeText(key)
                                          .then(() => {
                                            setCopiedKey(key); // Set the copied key
                                            setTimeout(
                                              () => setCopiedKey(null),
                                              2000
                                            ); // Reset after 2 seconds
                                          });
                                      }}
                                      className={`ml-2 rounded-md px-2 py-1 text-xs font-medium shadow-sm ${
                                        copiedKey === key
                                          ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200"
                                          : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                                      }`}
                                    >
                                      {copiedKey === key ? (
                                        "🎉"
                                      ) : (
                                        <Copy className="h-4 w-4 inline-block" />
                                      )}
                                    </button>
                                  </div>
                                </dt>
                                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex">
                                  <div className="truncate dark:text-zinc-500 w-5/6">
                                    {value}
                                  </div>
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="">
                    <div className="mt-4">
                      {/* <label
                        htmlFor="subject"
                        className="block text-sm/6 font-medium dark:text-white mb-2"
                      >
                        Body
                      </label> */}
                      <div className="w-[40rem]">
                        <Quill name="body" />
                      </div>
                    </div>
                    <div className="flex space-x-3 mt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        onClick={() => setOpen(false)} // Close modal on cancel
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Send
                        <Send className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};
